import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2, X } from 'lucide-react';

import {
  isLockedVisit,
  QUAY_BOLLARDS,
} from '@/components/blocks/berth-plan/berth-plan.utils';
import { FormTextInput } from '@/components/form-fields/form-text-input';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { formatDateForInput } from '@/lib/utils';
import { toVisitPatch } from '@/routes/(app)/(scenarios)/(create)/components/shipment-planning.utils';
import {
  EditShipVisitSchema,
  getEditShipVisitSchema,
  ShipVisit,
} from '@/routes/(app)/(scenarios)/(create)/types';

interface ShipVisitEditorProps {
  visit: ShipVisit;
  cutoffMs?: number;
  hasConflict: boolean;
  /** Only for visits not on the plan; placed visits move by dragging. */
  canEditBollards: boolean;
  onSave: (patch: Partial<ShipVisit>) => Promise<void>;
  onDelete: () => void;
  onClose: () => void;
}

/** Popover body for editing one visit; Locked Visits are shown read-only. */
const ShipVisitEditor: FC<ShipVisitEditorProps> = ({
  visit,
  cutoffMs,
  hasConflict,
  canEditBollards,
  onSave,
  onDelete,
  onClose,
}) => {
  const { t } = useTranslation();
  const isLocked = isLockedVisit(visit, cutoffMs);
  const schema = useMemo(
    () => getEditShipVisitSchema(cutoffMs, canEditBollards),
    [cutoffMs, canEditBollards]
  );
  // Kept to diff against on submit, so only changed fields are saved.
  const [initialValues] = useState<EditShipVisitSchema>(() => ({
    arrival_time: formatDateForInput(visit.arrival_time),
    departure_time: formatDateForInput(visit.departure_time ?? ''),
    // Left out when hidden, so an off-quay value can't fail validation.
    bollard_fore: canEditBollards
      ? (visit.bollard_fore ?? undefined)
      : undefined,
    bollard_aft: canEditBollards ? (visit.bollard_aft ?? undefined) : undefined,
    n_moves_load: visit.n_moves_load,
    n_moves_discharge: visit.n_moves_discharge,
  }));
  const form = useForm<EditShipVisitSchema>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const handleSubmit = async (values: EditShipVisitSchema) => {
    const patch = toVisitPatch(values, initialValues);
    // Closes right away; the save lands in the cache first, then the server.
    onClose();
    if (Object.keys(patch).length > 0) await onSave(patch);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className='flex flex-col gap-3'>
        <div className='flex items-start justify-between gap-2'>
          <div className='min-w-0'>
            <p className='truncate font-semibold'>{visit.ship_id}</p>
            <p className='truncate text-xs text-muted-foreground'>
              {t('scenarios.create.visitEditor.idService', {
                id: visit.id,
                service: visit.shipping_line_id,
              })}
            </p>
          </div>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='size-7 shrink-0'
            aria-label={t('scenarios.create.visitEditor.close')}
            onClick={onClose}>
            <X />
          </Button>
        </div>
        {hasConflict && (
          <p className='rounded-md bg-destructive/10 px-2.5 py-2 text-xs text-destructive'>
            {t('scenarios.create.visitEditor.conflict')}
          </p>
        )}
        {isLocked && (
          <p className='rounded-md bg-amber-500/10 px-2.5 py-2 text-xs text-amber-700'>
            {t('scenarios.create.visitEditor.locked')}
          </p>
        )}
        <div className='grid grid-cols-2 gap-3'>
          {canEditBollards && (
            <>
              <FormTextInput
                type='number'
                control={form.control}
                name='bollard_fore'
                label={t('shipVisits.table.bollard_fore')}
                min={1}
                max={QUAY_BOLLARDS}
                disabled={isLocked}
              />
              <FormTextInput
                type='number'
                control={form.control}
                name='bollard_aft'
                label={t('shipVisits.table.bollard_aft')}
                min={1}
                max={QUAY_BOLLARDS}
                disabled={isLocked}
              />
            </>
          )}
          <FormTextInput
            type='datetime-local'
            control={form.control}
            name='arrival_time'
            label={t('shipVisits.table.arrival_time')}
            className='col-span-2'
            disabled={isLocked}
          />
          <FormTextInput
            type='datetime-local'
            control={form.control}
            name='departure_time'
            label={t('shipVisits.table.departure_time')}
            className='col-span-2'
            disabled={isLocked}
          />
          <FormTextInput
            type='number'
            control={form.control}
            name='n_moves_discharge'
            label={t('shipVisits.table.n_moves_discharge')}
            min={0}
            disabled={isLocked}
          />
          <FormTextInput
            type='number'
            control={form.control}
            name='n_moves_load'
            label={t('shipVisits.table.n_moves_load')}
            min={0}
            disabled={isLocked}
          />
        </div>
        {!isLocked && (
          <div className='flex items-center justify-between gap-2'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='text-destructive hover:bg-destructive/10 hover:text-destructive'
              onClick={onDelete}>
              <Trash2 />
              {t('scenarios.create.visitEditor.delete')}
            </Button>
            <Button
              type='submit'
              size='sm'>
              {t('scenarios.create.visitEditor.save')}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

export default ShipVisitEditor;
